import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import {
  ESTADOS_SOLICITUD,
  ETIQUETA_ESTADO_SOLICITUD,
  formatearFechaHora,
  type EstadoActivo,
  type EstadoSolicitud,
  type Pagina,
  type Prioridad,
} from '@cmms/shared';
import { api, consulta, ErrorApi } from '../servicios/api.js';
import { ErroresFormulario } from '../componentes/piezas.js';
import { BotonConsolidadoMateriales } from '../componentes/consolidado-materiales.js';
import { useSesion } from '../servicios/sesion.js';
import {
  Aviso,
  Cargando,
  CodigoQr,
  Dialogo,
  DialogoConfirmar,
  EstadoActivoEtiqueta,
  EstadoSolicitudEtiqueta,
  GaleriaImagenes,
  Indicador,
  MiniaturaImagenTabla,
  Paginacion,
  PrioridadEtiqueta,
  SubidaImagenes,
  TONO_ESTADO_SOLICITUD,
  Vacio,
  mensajeError,
  type AdjuntoImagen,
} from '../componentes/piezas.js';
import {
  SelectorLista,
  SelectorUbicacion,
  useTiposFallaDeActivo,
  useUbicaciones,
} from '../componentes/selectores.js';
import { BotonVolver } from '../componentes/boton-volver.js';
import { BotonEscanearQr } from '../componentes/camara.js';
import { IconoLupa, IconoMas } from '../componentes/iconos.js';
import { useEsMovil } from '../servicios/responsive.js';
import { EnlaceRuta } from '../componentes/enlace-ruta.js';
import { PlanMaterialesSolicitud } from '../componentes/plan-materiales-solicitud.js';

interface Solicitud {
  id: string;
  numero: number;
  titulo: string;
  descripcion: string;
  ubicacion: string | null;
  conflictoSede: boolean;
  sedeConflicto: string | null;
  ubicacionActivo: string | null;
  ubicacionId: string | null;
  activo: string | null;
  activoId: string | null;
  estadoActivo: string | null;
  prioridad: Prioridad;
  estado: EstadoSolicitud;
  solicitadoPorNombre: string | null;
  /** Nombre de quien la hizo, cuando se creó desde una cuenta compartida (§ pedido). */
  solicitantePersonaNombre: string | null;
  solicitantePersonaApellidos: string | null;
  solicitadoEl: string;
  resueltoPorNombre: string | null;
  resueltoEl: string | null;
  motivoDeclinacion: string | null;
  ordenTrabajoId: string | null;
  ordenTrabajoNumero: number | null;
  diasEnBandeja: number;
  antigua: boolean;
  fotoUrl: string | null;
}

export function ListaSolicitudes(): JSX.Element {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const esMovil = useEsMovil();
  const cliente = useQueryClient();
  const [parametrosUrl] = useSearchParams();
  const [pagina, setPagina] = useState(1);
  const [estado, setEstado] = useState(parametrosUrl.get('estado') ?? 'PENDIENTE');
  // Viene del dashboard («Ver solicitudes» de los tipos elegidos): lista de ids separados por coma.
  const tipoActivoId = parametrosUrl.get('tipoActivoId') ?? '';
  const [ubicacionId, setUbicacionId] = useState('');
  const [buscar, setBuscar] = useState('');
  const [modalFiltrosAbierto, setModalFiltrosAbierto] = useState(false);

  const { data: ubicaciones } = useUbicaciones();
  const cantidadFiltrosAplicados =
    (estado !== '' ? 1 : 0) + (ubicacionId !== '' ? 1 : 0) + (buscar.trim() !== '' ? 1 : 0);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['solicitudes', pagina, estado, ubicacionId, buscar, tipoActivoId],
    queryFn: () =>
      api.get<Pagina<Solicitud>>(
        `/solicitudes${consulta({ pagina, tamano: 25, estado, ubicacionId, buscar, tipoActivoId })}`,
      ),
  });

  // Borrado masivo (§ pedido, solo Administrador vía `solicitud:eliminar`). Sin
  // jerarquía que cascadear: a diferencia de las OT, una solicitud no tiene hijas.
  const puedeEliminar = puede('solicitud:eliminar');
  // La selección también sirve para el consolidado de materiales: la ven quienes pueden consolidar.
  const puedeConsolidar = puede('ot:leer') && puede('solicitud:leer');
  const puedeSeleccionar = puedeEliminar || puedeConsolidar;
  const [seleccionadas, setSeleccionadas] = useState<Set<string>>(new Set());
  const [mostrarConfirmarEliminar, setMostrarConfirmarEliminar] = useState(false);
  const eliminarSeleccionadas = useMutation({
    mutationFn: (ids: string[]) => api.post<{ eliminadas: number }>('/solicitudes/eliminar-masivo', { ids }),
    onSuccess: () => {
      setSeleccionadas(new Set());
      setMostrarConfirmarEliminar(false);
      void cliente.invalidateQueries({ queryKey: ['solicitudes'] });
      void cliente.invalidateQueries({ queryKey: ['bandeja-resumen'] });
    },
  });
  const alternarSeleccion = (id: string): void =>
    setSeleccionadas((s) => {
      const copia = new Set(s);
      if (copia.has(id)) copia.delete(id);
      else copia.add(id);
      return copia;
    });
  const idsDeLaPagina = (data?.datos ?? []).map((s) => s.id);
  const todasSeleccionadas = idsDeLaPagina.length > 0 && idsDeLaPagina.every((id) => seleccionadas.has(id));

  const { data: bandeja } = useQuery({
    queryKey: ['bandeja-resumen'],
    queryFn: () => api.get<{ estado: EstadoSolicitud; total: number; antiguas: number }[]>('/solicitudes/bandeja'),
    enabled: puede('solicitud:leer_todas'),
  });

  const pendientes = bandeja?.find((b) => b.estado === 'PENDIENTE');

  return (
    <>
      {tipoActivoId ? (
        <div className="campo-nota" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <span>Mostrando solo las solicitudes de los tipos de activo elegidos en el dashboard.</span>
          <button type="button" className="boton-enlace" onClick={() => navegar(`/solicitudes?estado=${estado}`)}>
            Quitar filtro de tipo
          </button>
        </div>
      ) : null}
      <div className="cabecera-pagina cabecera-lista">
        <div className="cabecera-lista-titulo">
          <h1>Solicitudes</h1>
          {puede('solicitud:leer_todas') ? (
            <Link to="/solicitudes/conflictos" className="boton secundario pequeno">
              Conflictos de sede
            </Link>
          ) : null}
        </div>
        <div className="campo cabecera-lista-buscar">
          <IconoLupa />
          <input
            aria-label="Buscar solicitudes"
            placeholder="Buscar Solicitudes"
            value={buscar}
            onChange={(e) => {
              setBuscar(e.target.value);
              setPagina(1);
            }}
          />
        </div>
        {puede('solicitud:crear') ? (
          <>
            <Link className="boton oculto-en-movil" to="/solicitudes/nueva">
              + Nueva Solicitud
            </Link>
            <Link to="/solicitudes/nueva" className="boton-flotante-crear" aria-label="Nueva solicitud">
              <IconoMas />
            </Link>
          </>
        ) : null}
      </div>

      {bandeja ? (
        <div className="rejilla-indicadores">
          {ESTADOS_SOLICITUD.map((e) => {
            const fila = bandeja.find((b) => b.estado === e);
            return (
              <Indicador
                key={e}
                titulo={ETIQUETA_ESTADO_SOLICITUD[e].toUpperCase()}
                valor={fila?.total ?? 0}
                tono={TONO_ESTADO_SOLICITUD[e]}
                activo={estado === e}
                className={e === 'APROBADA' ? 'col-ocultar-movil' : undefined}
                nota={
                  e === 'PENDIENTE' && fila?.antiguas
                    ? `${fila.antiguas} llevan demasiado tiempo sin resolver`
                    : undefined
                }
                onClick={() => {
                  setEstado((actual) => (actual === e ? '' : e));
                  setPagina(1);
                }}
              />
            );
          })}
        </div>
      ) : null}

      {/* §9.4: hoy nada obliga a depurar la bandeja, y hay solicitudes sin
          resolver desde hace más de un año. El aviso hace visible ese hueco. */}
      {pendientes?.antiguas ? (
        <Aviso tono="atencion">
          {pendientes.antiguas} solicitud(es) pendiente(s) llevan demasiado tiempo sin resolver.
          Revíselas o decline las que ya no apliquen.
        </Aviso>
      ) : null}

      <button type="button" className="boton secundario solo-movil boton-filtros-movil" onClick={() => setModalFiltrosAbierto(true)}>
        Filtros{cantidadFiltrosAplicados > 0 ? ` (${cantidadFiltrosAplicados})` : ''}
      </button>

      <div className="filtros filtros-escritorio">
        <SelectorLista
          id="solicitudes-filtro-estado"
          etiqueta="Estado"
          valor={estado}
          alCambiar={(v) => {
            setEstado(v);
            setPagina(1);
          }}
          opciones={ESTADOS_SOLICITUD.map((e) => ({ id: e, nombre: ETIQUETA_ESTADO_SOLICITUD[e] }))}
          textoVacio="Todas"
        />
        <SelectorUbicacion
          id="solicitudes-filtro-ubicacion"
          valor={ubicacionId}
          alCambiar={(v) => {
            setUbicacionId(v);
            setPagina(1);
          }}
          ubicaciones={ubicaciones}
        />
        {puedeSeleccionar && idsDeLaPagina.length > 0 ? (
          <button
            type="button"
            className="boton secundario pequeno"
            style={{ alignSelf: 'flex-end' }}
            onClick={() =>
              setSeleccionadas((s) =>
                todasSeleccionadas
                  ? new Set([...s].filter((id) => !idsDeLaPagina.includes(id)))
                  : new Set([...s, ...idsDeLaPagina]),
              )
            }
          >
            {todasSeleccionadas ? 'Deseleccionar todas' : 'Seleccionar todas'}
          </button>
        ) : null}
      </div>

      {modalFiltrosAbierto ? (
        <Dialogo titulo="Filtros" alCerrar={() => setModalFiltrosAbierto(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <SelectorLista
              id="mf-solicitudes-estado"
              etiqueta="Estado"
              valor={estado}
              alCambiar={(v) => {
                setEstado(v);
                setPagina(1);
              }}
              opciones={ESTADOS_SOLICITUD.map((e) => ({ id: e, nombre: ETIQUETA_ESTADO_SOLICITUD[e] }))}
              textoVacio="Todas"
            />
            <SelectorUbicacion
              id="mf-solicitudes-ubicacion"
              valor={ubicacionId}
              alCambiar={(v) => {
                setUbicacionId(v);
                setPagina(1);
              }}
              ubicaciones={ubicaciones}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="boton" onClick={() => setModalFiltrosAbierto(false)}>
                Listo
              </button>
            </div>
          </div>
        </Dialogo>
      ) : null}

      <div className="panel">
        {isLoading ? <Cargando /> : null}
        {isError ? <Aviso tono="error">{mensajeError(error, 'No se pudieron cargar las solicitudes.')}</Aviso> : null}
        {data && data.datos.length === 0 ? <Vacio titulo="Sin solicitudes en esta vista" /> : null}

        {data && data.datos.length > 0 ? (
          <>
            {esMovil ? (
              <div className="tarjetas-ot">
                {data.datos.map((s) => (
                  <div
                    key={s.id}
                    className="tarjeta-ot"
                    role="button"
                    tabIndex={0}
                    onClick={() => navegar(`/solicitudes/${s.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navegar(`/solicitudes/${s.id}`);
                    }}
                  >
                    <MiniaturaImagenTabla url={s.fotoUrl} nombre={s.titulo} />
                    <div className="tarjeta-ot-cuerpo">
                      <div className="tarjeta-ot-cabecera">
                        <span className="numero-registro">#{s.numero}</span>
                        <EstadoSolicitudEtiqueta estado={s.estado} />
                      </div>
                      <div className="tarjeta-ot-titulo">{s.titulo}</div>
                      <div className="tarjeta-ot-datos">
                        {s.prioridad !== 'NINGUNA' ? <PrioridadEtiqueta prioridad={s.prioridad} /> : null}
                        {s.ubicacion ? <span>{s.ubicacion}</span> : null}
                        <span style={s.antigua ? { color: 'var(--alerta)', fontWeight: 700 } : undefined}>
                          {s.diasEnBandeja} día(s)
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tabla-contenedor">
                <table className="tabla">
                  <thead>
                    <tr>
                      {puedeSeleccionar ? (
                        <th>
                          <input
                            type="checkbox"
                            checked={todasSeleccionadas}
                            onChange={() =>
                              setSeleccionadas((s) => {
                                if (todasSeleccionadas) {
                                  const copia = new Set(s);
                                  for (const id of idsDeLaPagina) copia.delete(id);
                                  return copia;
                                }
                                return new Set([...s, ...idsDeLaPagina]);
                              })
                            }
                            aria-label="Seleccionar todas las solicitudes de esta página"
                            title="Seleccionar todas"
                          />
                        </th>
                      ) : null}
                      <th>Foto</th>
                      <th>N.º</th>
                      <th>Título</th>
                      <th>Estado</th>
                      <th>Prioridad</th>
                      <th>Ubicación</th>
                      <th>Activo</th>
                      <th>Solicitante</th>
                      <th>Antigüedad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.datos.map((s) => (
                      <tr key={s.id}>
                        {puedeSeleccionar ? (
                          <td>
                            <input
                              type="checkbox"
                              checked={seleccionadas.has(s.id)}
                              onChange={() => alternarSeleccion(s.id)}
                              aria-label={`Seleccionar solicitud #${s.numero}`}
                            />
                          </td>
                        ) : null}
                        <td>
                          <MiniaturaImagenTabla url={s.fotoUrl} nombre={s.titulo} />
                        </td>
                        <td className="numero-registro">#{s.numero}</td>
                        <td>
                          <Link to={`/solicitudes/${s.id}`} style={{ fontWeight: 600 }}>
                            {s.titulo}
                          </Link>
                        </td>
                        <td>
                          <EstadoSolicitudEtiqueta estado={s.estado} />
                        </td>
                        <td>
                          <PrioridadEtiqueta prioridad={s.prioridad} />
                        </td>
                        <td>
                          {s.ubicacion ?? '—'}
                          {s.conflictoSede ? (
                            <span
                              title="La ubicación quedó fuera de la sede del QR. Revísela en Conflictos de sede."
                              style={{
                                marginLeft: 8,
                                padding: '2px 8px',
                                borderRadius: 999,
                                fontSize: 11.5,
                                fontWeight: 700,
                                background: 'var(--alerta)',
                                color: '#fff',
                              }}
                            >
                              Conflicto de sede
                            </span>
                          ) : null}
                        </td>
                        <td>{s.activo ?? '—'}</td>
                        <td>{s.solicitadoPorNombre ?? '—'}</td>
                        <td style={s.antigua ? { color: 'var(--alerta)', fontWeight: 700 } : undefined}>
                          {s.diasEnBandeja} día(s)
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Paginacion pagina={data.pagina} tamano={data.tamano} total={data.total} onCambiar={setPagina} />
          </>
        ) : null}
      </div>
      {puedeSeleccionar && seleccionadas.size > 0 ? (
        <div className="barra-acciones-fija">
          <span className="barra-acciones-fija-estado">
            <span className="barra-acciones-fija-punto" />
            {seleccionadas.size} solicitud(es) seleccionada(s)
          </span>
          <div className="barra-acciones-fija-botones">
            <button type="button" className="boton secundario pequeno" onClick={() => setSeleccionadas(new Set())}>
              Cancelar selección
            </button>
            {puedeConsolidar ? (
              <BotonConsolidadoMateriales
                etiqueta="Ver materiales consolidados"
                elegidas={[...seleccionadas].map((id) => `SOLICITUD:${id}`)}
              />
            ) : null}
            {puedeEliminar ? (
              <button type="button" className="boton peligro pequeno" onClick={() => setMostrarConfirmarEliminar(true)}>
                Eliminar seleccionadas
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {mostrarConfirmarEliminar ? (
        <DialogoConfirmar
          titulo="Eliminar solicitudes"
          mensaje={`Se eliminarán ${seleccionadas.size} solicitud(es). Esta acción no se puede deshacer desde aquí.`}
          etiqueta="Eliminar"
          cargando={eliminarSeleccionadas.isPending}
          error={eliminarSeleccionadas.error}
          alCerrar={() => setMostrarConfirmarEliminar(false)}
          alConfirmar={() => eliminarSeleccionadas.mutate(Array.from(seleccionadas))}
        />
      ) : null}
    </>
  );
}

export function DetalleSolicitud(): JSX.Element {
  const { id = '' } = useParams();
  const cliente = useQueryClient();
  const { puede } = useSesion();
  const [motivo, setMotivo] = useState('');
  const [declinando, setDeclinando] = useState(false);
  const [modalUbicacionId, setModalUbicacionId] = useState<string | null>(null);
  const [modalActivoId, setModalActivoId] = useState<string | null>(null);

  const { data: solicitud, isLoading } = useQuery({
    queryKey: ['solicitud', id],
    queryFn: () => api.get<Solicitud>(`/solicitudes/${id}`),
  });

  const { data: fotos } = useQuery({
    queryKey: ['adjuntos', 'SOLICITUD', id],
    queryFn: () => api.get<AdjuntoImagen[]>(`/adjuntos${consulta({ entidad: 'SOLICITUD', entidadId: id })}`),
  });

  const declinar = useMutation({
    mutationFn: () => api.post(`/solicitudes/${id}/declinar`, { motivo }),
    onSuccess: async () => {
      setDeclinando(false);
      await cliente.invalidateQueries({ queryKey: ['solicitud', id] });
      await cliente.invalidateQueries({ queryKey: ['bandeja-resumen'] });
      await cliente.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const restablecer = useMutation({
    mutationFn: () => api.post(`/solicitudes/${id}/restablecer`),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['solicitud', id] });
      await cliente.invalidateQueries({ queryKey: ['bandeja-resumen'] });
      await cliente.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  if (isLoading) return <Cargando />;
  if (!solicitud) return <Vacio titulo="La solicitud no existe" />;

  const puedeResolver = puede('solicitud:resolver') && solicitud.estado === 'PENDIENTE';

  return (
    <>
      <BotonVolver a="/solicitudes" etiqueta="Volver a Solicitudes" />

      <div className="cabecera-pagina">
        <div>
          <div className="numero-registro">SOLICITUD #{solicitud.numero}</div>
          <h1>{solicitud.titulo}</h1>
        </div>
        <PrioridadEtiqueta prioridad={solicitud.prioridad} />
      </div>

      {declinar.isError ? <ErroresFormulario error={declinar.error} /> : null}

      {solicitud.ordenTrabajoId ? (
        <Aviso tono="exito">
          Aprobada por {solicitud.resueltoPorNombre ?? '—'}. Generó la{' '}
          {puede('ot:leer') ? (
            <EnlaceRuta to={`/ordenes/${solicitud.ordenTrabajoId}`}>orden de trabajo #{solicitud.ordenTrabajoNumero}</EnlaceRuta>
          ) : (
            // Sin `ot:leer` (ej. un Solicitante) el enlace llevaría a una ruta
            // que ni siquiera se registra y lo devolvería al dashboard sin
            // explicación — se muestra el número, sin prometer una navegación
            // que no puede cumplir.
            <>orden de trabajo #{solicitud.ordenTrabajoNumero}</>
          )}
          .
        </Aviso>
      ) : null}

      {solicitud.motivoDeclinacion ? (
        <Aviso tono="error">
          Declinada por {solicitud.resueltoPorNombre ?? '—'}: {solicitud.motivoDeclinacion}
          {puede('solicitud:resolver') ? (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="boton secundario pequeno"
                disabled={restablecer.isPending}
                onClick={() => restablecer.mutate()}
              >
                {restablecer.isPending ? 'Restableciendo…' : 'Restablecer solicitud'}
              </button>
            </div>
          ) : null}
          {restablecer.isError ? <ErroresFormulario error={restablecer.error} /> : null}
        </Aviso>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <section className="panel">
          <div className="panel-cuerpo">
            <div className="rejilla-campos" style={{ marginBottom: 0 }}>
              <DatoSolicitud titulo="Estado" valor={<EstadoSolicitudEtiqueta estado={solicitud.estado} />} />
              <DatoSolicitud
                titulo="Ubicación"
                valor={
                  !solicitud.ubicacionId ? (
                    solicitud.ubicacion ?? '—'
                  ) : puede('ubicacion:leer') ? (
                    <EnlaceRuta to={`/ubicaciones/${solicitud.ubicacionId}`}>{solicitud.ubicacion}</EnlaceRuta>
                  ) : (
                    <button
                      type="button"
                      className="boton-enlace"
                      onClick={() => setModalUbicacionId(solicitud.ubicacionId)}
                    >
                      {solicitud.ubicacion}
                    </button>
                  )
                }
              />
              {solicitud.conflictoSede ? (
                <DatoSolicitud
                  titulo="Conflicto de sede · revisar antes de aprobar"
                  valor={
                    <span style={{ color: 'var(--alerta)', fontWeight: 700, display: 'block', lineHeight: 1.6 }}>
                      Sede escaneada: {solicitud.sedeConflicto ?? '—'}
                      <br />
                      Reportada en: {solicitud.ubicacion ?? '—'}
                    </span>
                  }
                />
              ) : null}
            </div>
          </div>
        </section>

        {solicitud.activo ? (
          <section className="panel">
            <div className="panel-cuerpo">
              <div className="rejilla-campos" style={{ marginBottom: 0 }}>
                <DatoSolicitud
                  titulo="Activo"
                  valor={
                    !solicitud.activoId ? (
                      solicitud.activo
                    ) : puede('activo:leer') ? (
                      <EnlaceRuta to={`/activos/${solicitud.activoId}`}>{solicitud.activo}</EnlaceRuta>
                    ) : (
                      <button type="button" className="boton-enlace" onClick={() => setModalActivoId(solicitud.activoId)}>
                        {solicitud.activo}
                      </button>
                    )
                  }
                />
                <DatoSolicitud
                  titulo="Estado del activo"
                  valor={
                    solicitud.estadoActivo ? (
                      <EstadoActivoEtiqueta estado={solicitud.estadoActivo as EstadoActivo} />
                    ) : (
                      '—'
                    )
                  }
                />
              </div>
            </div>
          </section>
        ) : null}

        <section className="panel">
          <div className="panel-cabecera">
            <h2>Descripción</h2>
          </div>
          <div className="panel-cuerpo">
            <p style={{ marginTop: 0, whiteSpace: 'pre-wrap' }}>{solicitud.descripcion}</p>

            {fotos && fotos.length > 0 ? (
              <GaleriaImagenes fotos={fotos} titulo={(f) => `Solicitud #${solicitud.numero} · ${f.nombreArchivo}`} />
            ) : null}
          </div>
        </section>

        <section className="panel">
          <div className="panel-cuerpo">
            <div className="rejilla-campos" style={{ marginBottom: 0 }}>
              <DatoSolicitud
                titulo="Solicitada por"
                valor={`${solicitud.solicitadoPorNombre ?? '—'} en ${formatearFechaHora(solicitud.solicitadoEl)}`}
              />
              {solicitud.solicitantePersonaNombre ? (
                <DatoSolicitud
                  titulo="Persona que reportó"
                  valor={[solicitud.solicitantePersonaNombre, solicitud.solicitantePersonaApellidos].filter(Boolean).join(' ')}
                />
              ) : null}
              <DatoSolicitud
                titulo="Última actualización"
                valor={
                  solicitud.resueltoEl
                    ? `${solicitud.resueltoPorNombre ?? '—'} en ${formatearFechaHora(solicitud.resueltoEl)}`
                    : `En ${formatearFechaHora(solicitud.solicitadoEl)}`
                }
              />
              <DatoSolicitud
                titulo="Días en bandeja"
                valor={
                  <span style={solicitud.antigua ? { color: 'var(--alerta)', fontWeight: 700 } : undefined}>
                    {solicitud.diasEnBandeja} día(s){solicitud.antigua ? ' · supera el umbral' : ''}
                  </span>
                }
              />
            </div>
          </div>
        </section>

        {puede('ot:crear') ? (
          <PlanMaterialesSolicitud
            solicitudId={solicitud.id}
            activoId={solicitud.activoId}
            editable={solicitud.estado === 'PENDIENTE'}
          />
        ) : null}

        {puedeResolver ? (
          <section className="panel">
            <div className="panel-cuerpo">
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button type="button" className="boton secundario" onClick={() => setDeclinando((v) => !v)}>
                  Declinar solicitud
                </button>
                <EnlaceRuta
                  className="boton"
                  to={`/ordenes/nueva${consulta({
                    solicitudId: solicitud.id,
                    titulo: solicitud.titulo,
                    descripcion: solicitud.descripcion,
                    ubicacionId: solicitud.ubicacionId ?? '',
                    activoId: solicitud.activoId ?? '',
                    prioridad: solicitud.prioridad,
                  })}`}
                >
                  Aprobar solicitud
                </EnlaceRuta>
              </div>

              {/* §9.4: la declinación exige motivo. Sin él, el solicitante no sabe
                  qué pasó con lo que reportó y vuelve a reportarlo. */}
              {declinando ? (
                <form
                  style={{ marginTop: 14 }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (motivo.trim()) declinar.mutate();
                  }}
                >
                  <div className="campo">
                    <label htmlFor="motivo">Motivo de la declinación</label>
                    <textarea
                      id="motivo"
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                      required
                      placeholder="Explique por qué no procede; se notificará al solicitante."
                    />
                  </div>
                  <button type="submit" className="boton peligro" disabled={!motivo.trim() || declinar.isPending}>
                    Confirmar declinación
                  </button>
                </form>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>

      {modalUbicacionId ? (
        <ModalResumenUbicacion id={modalUbicacionId} alCerrar={() => setModalUbicacionId(null)} />
      ) : null}
      {modalActivoId ? <ModalResumenActivo id={modalActivoId} alCerrar={() => setModalActivoId(null)} /> : null}
    </>
  );
}

function DatoSolicitud({ titulo, valor }: { titulo: string; valor: ReactNode }): JSX.Element {
  return (
    <div>
      <div className="indicador-titulo">{titulo.toUpperCase()}</div>
      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{valor}</div>
    </div>
  );
}

interface ResumenUbicacion {
  id: string;
  nombre: string;
  nombrePadre: string | null;
  direccionEfectiva: string | null;
}

/** Ficha mínima para quien no tiene `ubicacion:leer` (§ dashboard: no debe ver checklist ni conteos). */
function ModalResumenUbicacion({ id, alCerrar }: { id: string; alCerrar: () => void }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['ubicacion-resumen', id],
    queryFn: () => api.get<ResumenUbicacion>(`/ubicaciones/${id}/resumen`),
  });

  return (
    <Dialogo titulo="Ubicación" ancho={420} alCerrar={alCerrar}>
      {isLoading ? <Cargando /> : null}
      {data ? (
        <div>
          <strong style={{ fontSize: 16 }}>{data.nombre}</strong>
          {data.nombrePadre ? <p style={{ color: 'var(--texto-medio)', margin: '4px 0 0' }}>Sede: {data.nombrePadre}</p> : null}
          {data.direccionEfectiva ? (
            <p style={{ color: 'var(--texto-medio)', margin: '4px 0 0' }}>{data.direccionEfectiva}</p>
          ) : null}
        </div>
      ) : null}
    </Dialogo>
  );
}

interface ResumenActivo {
  id: string;
  nombre: string;
  ubicacionNombre: string | null;
  fotoUrl: string | null;
  codigoQr: string | null;
}

/** Ficha mínima para quien no tiene `activo:leer`: foto, nombre, sede y QR — nada de historial ni observaciones. */
function ModalResumenActivo({ id, alCerrar }: { id: string; alCerrar: () => void }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['activo-resumen', id],
    queryFn: () => api.get<ResumenActivo>(`/activos/${id}/resumen`),
  });

  return (
    <Dialogo titulo="Activo" ancho={420} alCerrar={alCerrar}>
      {isLoading ? <Cargando /> : null}
      {data ? (
        <div>
          {data.fotoUrl ? (
            <img
              src={data.fotoUrl}
              alt={data.nombre}
              style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 8, marginBottom: 12 }}
            />
          ) : null}
          <strong style={{ fontSize: 16 }}>{data.nombre}</strong>
          {data.ubicacionNombre ? (
            <p style={{ color: 'var(--texto-medio)', margin: '4px 0 0' }}>Sede: {data.ubicacionNombre}</p>
          ) : null}
          {data.codigoQr ? (
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
              <CodigoQr codigo={data.codigoQr} tamano={150} />
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialogo>
  );
}

/**
 * §16.2: escanear un QR desde «Leer QR» del dashboard puede caer directo acá
 * con el activo/ubicación ya resuelto, para no obligar a repetir a mano lo
 * que ya se sabía en el escaneo.
 */
/**
 * Formulario abierto desde el QR de un equipo, sin sesión (`/s/:codigo`). Usa el
 * mismo cuerpo; solo cambian las rutas de datos y que la ubicación y el activo
 * vienen fijados por el escaneo.
 */
export interface ModoPublico {
  codigo: string;
  /** Organización del enlace (`?t=`): sin ella, el código se busca en todas. */
  tenant?: string | null;
  inicial: { ubicacionId: string | null; activoId: string | null };
  alCrear: (numero: number) => void;
  /** Tablet emparejada (kiosko): el mismo formulario, con rutas /movil y el token del dispositivo. */
  movil?: boolean;
}

export function NuevaSolicitud({ publico }: { publico?: ModoPublico } = {}): JSX.Element {
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const [parametros] = useSearchParams();
  // Desde el QR público la ubicación y el activo ya vienen resueltos por el escaneo.
  // Todas las llamadas públicas llevan la organización del enlace (`?t=`), si la trae.
  const qT = publico?.tenant ? `?t=${encodeURIComponent(publico.tenant)}` : '';
  const movil = !!publico?.movil;
  // Prefijo de las rutas públicas: la del QR de la sede, o /movil para la tablet.
  const base = publico ? (movil ? '/movil' : `/publico/qr/${publico.codigo}`) : '';
  const rutaSolicitud = (sufijo: string): string =>
    publico ? `${base}/${sufijo}${qT}` : `/solicitudes/${sufijo}`;
  const [titulo, setTitulo] = useState(parametros.get('titulo') ?? '');
  const [descripcion, setDescripcion] = useState('');
  const [tipoFallaId, setTipoFallaId] = useState('');
  const [otraFalla, setOtraFalla] = useState(false);
  const [ubicacionId, setUbicacionId] = useState(publico?.inicial.ubicacionId ?? parametros.get('ubicacionId') ?? '');
  const [activoId, setActivoId] = useState(publico?.inicial.activoId ?? parametros.get('activoId') ?? '');
  const [fotos, setFotos] = useState<AdjuntoImagen[]>([]);
  const [avisoUbicacion, setAvisoUbicacion] = useState('');
  const [nombrePersona, setNombrePersona] = useState('');
  const [apellidosPersona, setApellidosPersona] = useState('');

  const { usuario } = useSesion();
  // `/solicitudes/ubicaciones`, no `useUbicaciones()`: el árbol de ubicaciones
  // exige `ubicacion:leer`, que un consultante no tiene; sin él no vería ni la
  // ubicación que trae el QR.
  const { data: ubicaciones } = useQuery({
    queryKey: ['solicitudes-ubicaciones', publico?.codigo ?? ''],
    queryFn: () => api.get<{ id: string; nombre: string; padreId: string | null }[]>(rutaSolicitud('ubicaciones')),
  });
  // Un consultante siempre indica dónde ocurre la falla; el nombre de quien
  // reporta es obligatorio en una cuenta compartida (p. ej. la de una sede) y
  // siempre desde el QR público, donde la cuenta que firma es de sistema.
  const esConsultante = publico ? true : usuario?.tipoAcceso === 'SOLICITANTE';
  const nombreObligatorio = publico ? !movil : !!usuario?.esCompartido;

  // §9.4: seleccionada la ubicación, el desplegable de activos se filtra a los
  // de esa sede. Evita que se reporte una falla contra un equipo de otro local.
  const { data: activos } = useQuery({
    queryKey: ['activos-de', publico?.codigo ?? '', ubicacionId],
    queryFn: () => api.get<{ id: string; nombre: string }[]>(rutaSolicitud(`activos-de/${ubicacionId}`)),
    enabled: ubicacionId !== '',
  });

  // §9.4b: el activo puede traer más de un tipo; se usa el primero para
  // resolver sus fallas típicas y ofrecerlas en vez de un texto libre.
  // `/resumen`, no `/activos/:id` a secas: esta pantalla la usa cualquiera
  // que pueda crear una solicitud, incluido un Solicitante sin `activo:leer`
  // — la ficha completa le devolvería 403.
  const { data: activoDetalle } = useQuery({
    queryKey: ['activo-detalle-solicitud', publico?.codigo ?? '', activoId],
    queryFn: () =>
      api.get<{ tiposActivo: { id: string; nombre: string }[] }>(
        publico ? `${base}/activos/${activoId}/resumen${qT}` : `/activos/${activoId}/resumen`,
      ),
    enabled: activoId !== '',
  });
  const tipoActivoId = activoDetalle?.tiposActivo[0]?.id ?? '';
  const { data: fallasDelTipo } = useTiposFallaDeActivo(tipoActivoId, publico ? `${base}${qT}` : undefined);

  const crear = useMutation({
    mutationFn: async () => {
      const cuerpo = {
        titulo,
        descripcion,
        ubicacionId: ubicacionId || null,
        activoId: activoId || null,
        adjuntos: fotos.map((f) => f.id),
        nombrePersona: nombreObligatorio ? nombrePersona.trim() : undefined,
        apellidosPersona: nombreObligatorio ? apellidosPersona.trim() : undefined,
      };
      if (publico) return api.post<{ numero: number }>(movil ? '/movil/solicitudes' : `${base}${qT}`, cuerpo);
      return api.post<Solicitud>('/solicitudes', cuerpo);
    },
    onSuccess: async (resultado) => {
      if (publico) {
        publico.alCrear((resultado as { numero: number }).numero);
        return;
      }
      const solicitud = resultado as Solicitud;
      await cliente.invalidateQueries({ queryKey: ['solicitudes'] });
      await cliente.invalidateQueries({ queryKey: ['bandeja-resumen'] });
      await cliente.invalidateQueries({ queryKey: ['dashboard'] });
      // `replace`: el formulario ya enviado no debe quedar en el historial; «atrás» vuelve a donde estaba antes de crear.
      navegar(`/solicitudes/${solicitud.id}`, { replace: true });
    },
  });

  return (
    <>
      {publico ? null : <BotonVolver a="/solicitudes" etiqueta="Volver a Solicitudes" />}

      <div className="cabecera-pagina">
        <div>
          <h1>Nueva solicitud</h1>
        </div>
        <BotonEscanearQr
          etiqueta="Escanear QR de activo o ubicación"
          entidades={['ACTIVO', 'UBICACION']}
          rutaResolver={publico ? (movil ? '/movil/qr' : `/publico/qr${qT}`) : undefined}
          alResolver={(r) => {
            setTipoFallaId('');
            setOtraFalla(false);
            if (r.entidad === 'UBICACION') {
              setUbicacionId(r.entidadId);
              setActivoId('');
              setAvisoUbicacion('');
            } else {
              setActivoId(r.entidadId);
              setAvisoUbicacion('');
              if (r.ubicacionId) {
                setUbicacionId(r.ubicacionId);
              } else {
                // La resolución del QR puede no traer la ubicación (API
                // previa al cambio): se pide a la ficha resumida del activo
                // (no `/activos/:id` a secas — un Solicitante no tiene
                // `activo:leer` y esa ruta le devolvería 403).
                void api
                  .get<{ ubicacionId: string | null }>(
                    publico
                      ? `${base}/activos/${r.entidadId}/resumen${qT}`
                      : `/activos/${r.entidadId}/resumen`,
                  )
                  .then((a) => {
                    if (a.ubicacionId) {
                      setUbicacionId(a.ubicacionId);
                    } else {
                      setAvisoUbicacion(
                        'El activo escaneado no tiene una ubicación asignada. Selecciónela manualmente.',
                      );
                    }
                  })
                  .catch(() =>
                    setAvisoUbicacion('No se pudo obtener la ubicación del activo. Selecciónela manualmente.'),
                  );
              }
            }
            // Prellenado de cortesía solo para ubicación: al escanear un activo,
            // la descripción se decide con el selector de fallas típicas.
            if (r.entidad === 'UBICACION' && descripcion.trim() === '') {
              setDescripcion(`Incidencia reportada en la ubicación ${r.nombre}.`);
            } else if (r.entidad === 'ACTIVO') {
              setDescripcion('');
            }
          }}
        />
      </div>

      {avisoUbicacion ? <Aviso tono="atencion">{avisoUbicacion}</Aviso> : null}
      {crear.isError ? <ErroresFormulario error={crear.error} /> : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          crear.mutate();
        }}
      >
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Detalle</h2>
          </div>
          <div className="panel-cuerpo">
            <div className="campo">
              <label htmlFor="titulo">Título</label>
              <input
                id="titulo"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                required
                maxLength={250}
                placeholder="Ej.: Cabezal no enfría"
              />
            </div>

            {nombreObligatorio ? (
              <div className="campo">
                <label htmlFor="nombre-persona">Su nombre</label>
                <input
                  id="nombre-persona"
                  value={nombrePersona}
                  onChange={(e) => setNombrePersona(e.target.value)}
                  required
                  maxLength={200}
                  placeholder="Nombre de quien reporta"
                />
                <span className="campo-nota">
                  {publico
                    ? 'Indique quién hace la solicitud.'
                    : 'Esta es una cuenta compartida: indique quién hace la solicitud.'}
                </span>
              </div>
            ) : null}

            {nombreObligatorio ? (
              <div className="campo">
                <label htmlFor="apellidos-persona-solicitud">Sus apellidos</label>
                <input
                  id="apellidos-persona-solicitud"
                  value={apellidosPersona}
                  onChange={(e) => setApellidosPersona(e.target.value)}
                  required
                  maxLength={200}
                  placeholder="Apellidos de quien reporta"
                />
              </div>
            ) : null}

            {activoId && fallasDelTipo && fallasDelTipo.length > 0 && !otraFalla ? (
              <div className="campo" style={{ marginBottom: 0 }}>
                <SelectorLista
                  id="tipo-falla"
                  etiqueta="Falla"
                  valor={tipoFallaId}
                  alCambiar={(v) => {
                    if (v === '__otra__') {
                      setOtraFalla(true);
                      setTipoFallaId('');
                      setDescripcion('');
                      return;
                    }
                    setTipoFallaId(v);
                    const falla = fallasDelTipo.find((f) => f.id === v);
                    setDescripcion(falla?.nombre ?? '');
                  }}
                  opciones={[...fallasDelTipo.map((f) => ({ id: f.id, nombre: f.nombre })), { id: '__otra__', nombre: 'Otra (describir)' }]}
                  requerido
                />
              </div>
            ) : (
              <div className="campo" style={{ marginBottom: 0 }}>
                <label htmlFor="descripcion">Descripción</label>
                <textarea
                  id="descripcion"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  required
                  placeholder="Detalle qué ocurre, en qué consultorio y desde cuándo."
                />
                {otraFalla ? (
                  <button
                    type="button"
                    className="boton secundario pequeno"
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      setOtraFalla(false);
                      setDescripcion('');
                    }}
                  >
                    Elegir una falla de la lista
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-cabecera">
            <h2>Ubicación y activo</h2>
          </div>
          <div className="panel-cuerpo">
            <div className="rejilla-campos" style={{ marginBottom: 0 }}>
              {publico && !movil ? (
                // Desde el QR de una sede la ubicación ya es esa sede: no se cambia, y los
                // activos que se ofrecen son solo los de ella.
                <div className="campo">
                  <label htmlFor="ubicacion">Ubicación</label>
                  <input
                    id="ubicacion"
                    readOnly
                    disabled
                    value={ubicaciones?.find((u) => u.id === ubicacionId)?.nombre ?? '—'}
                  />
                </div>
              ) : (
                <SelectorUbicacion
                  id="ubicacion"
                  valor={ubicacionId}
                  alCambiar={(v) => {
                    setUbicacionId(v);
                    setActivoId('');
                  }}
                  requerido={esConsultante}
                  ubicaciones={ubicaciones}
                />
              )}

              <SelectorLista
                id="activo"
                etiqueta="Activo"
                valor={activoId}
                alCambiar={(v) => {
                  setActivoId(v);
                  setTipoFallaId('');
                  setOtraFalla(false);
                  setDescripcion('');
                }}
                opciones={(activos ?? []).map((a) => ({ id: a.id, nombre: a.nombre }))}
                textoVacio={ubicacionId ? 'Seleccione…' : 'Elija primero una ubicación'}
                deshabilitado={!ubicacionId}
              />
            </div>
          </div>
        </div>

        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-cabecera">
            <h2>Fotos de la incidencia</h2>
          </div>
          <div className="panel-cuerpo">
            <SubidaImagenes
              etiqueta=""
              valor={fotos}
              alCambiar={setFotos}
              rutaAdjuntos={publico ? `${base}/adjuntos${qT}` : '/adjuntos'}
            />
          </div>
        </div>

        <div className="acciones-formulario" style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button
            type="submit"
            className="boton"
            disabled={
              crear.isPending ||
              descripcion.trim() === '' ||
              (nombreObligatorio && (nombrePersona.trim() === '' || apellidosPersona.trim() === '')) ||
              (esConsultante && ubicacionId === '')
            }
          >
            {crear.isPending ? 'Enviando…' : 'Enviar solicitud'}
          </button>
          <button type="button" className="boton secundario" onClick={() => navegar(movil ? '/movil' : publico ? '/s' : '/solicitudes')}>
            Cancelar
          </button>
        </div>
      </form>
    </>
  );
}

interface ConflictoSedeLista {
  id: string;
  creadoEl: string;
  solicitudId: string;
  numero: number;
  titulo: string;
  solicitantePersonaNombre: string | null;
  sedeQr: string;
  ubicacionFormulario: string;
}

/**
 * Solicitudes llegadas desde el QR de una sede con la ubicación final fuera de
 * ella. Solo las ve quien puede leer todas las solicitudes; el solicitante que
 * las registró no recibe ninguna pista de esto.
 */
export function ConflictosSede(): JSX.Element {
  const cliente = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['conflictos-sede'],
    queryFn: () => api.get<ConflictoSedeLista[]>('/solicitudes/conflictos-sede'),
  });
  const revisar = useMutation({
    mutationFn: (id: string) => api.post(`/solicitudes/conflictos-sede/${id}/revisar`),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['conflictos-sede'] }),
  });

  return (
    <>
      <BotonVolver a="/solicitudes" etiqueta="Volver a Solicitudes" />
      <div className="cabecera-pagina">
        <div>
          <h1>Conflictos de sede</h1>
          <p className="campo-nota">
            Solicitudes registradas desde el QR de una sede, cuya ubicación final quedó fuera de esa sede.
          </p>
        </div>
      </div>

      {error ? <ErroresFormulario error={error} /> : null}
      {revisar.isError ? <ErroresFormulario error={revisar.error} /> : null}
      {isLoading ? <p>Cargando…</p> : null}
      {data && data.length === 0 ? <p>No hay conflictos pendientes.</p> : null}

      {(data ?? []).map((c) => (
        <div key={c.id} className="panel" style={{ marginBottom: 12 }}>
          <div className="panel-cuerpo">
            <strong>
              <Link to={`/solicitudes/${c.solicitudId}`}>Solicitud #{c.numero}</Link> · {c.titulo}
            </strong>
            <p className="campo-nota">
              Sede del QR: {c.sedeQr} · Ubicación registrada: {c.ubicacionFormulario}
              {c.solicitantePersonaNombre ? ` · Reportó: ${c.solicitantePersonaNombre}` : ''}
            </p>
            <button
              type="button"
              className="boton secundario pequeno"
              disabled={revisar.isPending}
              onClick={() => revisar.mutate(c.id)}
            >
              Marcar como revisado
            </button>
          </div>
        </div>
      ))}
    </>
  );
}
